import React, { PureComponent } from 'react';
import PropTypes from "prop-types";
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import { createTranslator } from "./translate";
import PaymentTermsTab from './payment-terms-tab';
import AmortizationTab from './amortization-tab';
import { addVAT, percentToDecimal } from './utils';

const ONE_YEAR_IN_MONTHS = 12;
const ONE_YEAR_IN_QUARTERS = 4;

const INFINITE = 1 / 0;

const DEFAULT_CURRENCY = {
  code: 'SEK',
  side: 'suffix',
  shortCode: 'kr',
};

function createNumberFormatter(locale = 'sv-SE', currency = DEFAULT_CURRENCY) {
  const formatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency.code,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  return function numberFormatter(value) {
    let v = value;
    if (v === INFINITE || Number.isNaN(v) || !v || v < 0) {
      v = null;
    }
    return formatter.format(v);
  }
}

function createPercentFormatter(locale = 'sv-SE') {
  const formatter = new Intl.NumberFormat(locale);
  return function percentFormatter(value) {
    if (value === INFINITE || Number.isNaN(value) || !value) {
      return '- %';
    }
    return `${formatter.format(value)} %`;
  }
}

export default class InterestCalculator extends PureComponent {

  constructor(props) {
    super(props);
    this.onChangeAcquisitionPrice = this.onChangeAcquisitionPrice.bind(this);
    this.onChangeTradeInIncVAT = this.onChangeTradeInIncVAT.bind(this);
    this.onChangeDownPayment = this.onChangeDownPayment.bind(this);
    this.onChangeInterest = this.onChangeInterest.bind(this);
    this.onChangeTerm = this.onChangeTerm.bind(this);
    this.onChangePeriodicity = this.onChangePeriodicity.bind(this);
    this.onSetupFeeChanged = this.onSetupFeeChanged.bind(this);
    this.onAdministrationFeeChanged = this.onAdministrationFeeChanged.bind(this);
    this.formatter = createNumberFormatter(props.locale, props.currency);
    this.percentFormatter = createPercentFormatter(props.locale);
    this.t = createTranslator(props.locale);
    this.state = {
      acquisitionPrice: props.acquisitionPrice,
      tradeInIncVAT: props.tradeInIncVAT,
      downPayment: props.downPayment,
      interest: props.interest,
      term: props.term,
      periodicity: props.periodicity,
      setupFee: props.setupFee,
      administrationFee: props.administrationFee,
      creditAmount: InterestCalculator.calculateCreditAmount(props.acquisitionPrice, props.tradeInIncVAT, props.downPayment, props.vat),
    };
  }

  componentWillReceiveProps(nextProps) {
    this.formatter = createNumberFormatter(nextProps.locale, nextProps.currency);
    this.percentFormatter = createPercentFormatter(nextProps.locale);
  }

  componentWillUnmount() {
    this.formatter = null;
    this.percentFormatter = null;
  }

  calculateAmortizationAmount() {
    const state = this.state;
    const multiplier = state.periodicity === 'month' ? 1 : 3;
    if (state.creditAmount) {
      return ((state.creditAmount - this.calculateIncreasedAmortizationAmount()) / state.term) * multiplier;
    }
    return 0;
  }

  calculateVAT(value) {
    const vat = percentToDecimal(this.props.vat);
    return value - (value / vat);
  }

  calculateIncreasedAmortizationAmount() {
    const state = this.state;
    return (this.calculateVAT(addVAT(state.acquisitionPrice, this.props.vat)) - this.calculateVAT(addVAT(state.tradeInIncVAT, this.props.vat)));
  }

  calculatePaymentMonthOneIncludingInterest() {
    const periods = this.state.periodicity === 'month' ? ONE_YEAR_IN_MONTHS : ONE_YEAR_IN_QUARTERS;
    return (this.state.creditAmount * (this.state.interest / 100)) / periods + this.calculateAmortizationAmount();
  }

  static calculateCreditAmount(acquisitionPrice, tradeInExVAT, downPayment, vat) {
    const vatDecimal = percentToDecimal(vat);
    const creditAmount = (acquisitionPrice * vatDecimal) - (tradeInExVAT * vatDecimal) - downPayment;
    if (creditAmount < 0) {
      return null;
    }
    return creditAmount;
  }

  onChangeTradeInIncVAT(tradeInIncVAT) {
    this.setState({ tradeInIncVAT, creditAmount: InterestCalculator.calculateCreditAmount(
        this.state.acquisitionPrice, tradeInIncVAT, this.state.downPayment, this.props.vat) });
  }

  onChangeDownPayment(downPayment) {
    this.setState({ downPayment, creditAmount: InterestCalculator.calculateCreditAmount(
        this.state.acquisitionPrice, this.state.tradeInIncVAT, downPayment, this.props.vat) });
  }

  onChangeAcquisitionPrice(acquisitionPrice) {
    this.setState({ acquisitionPrice, creditAmount: InterestCalculator.calculateCreditAmount(
        acquisitionPrice, this.state.tradeInIncVAT, this.state.downPayment, this.props.vat) });
  }

  onChangeCreditAmount(creditAmount) {
    this.setState({ creditAmount });
  }

  onChangeInterest(interest) {
    this.setState({ interest });
  }

  onChangeTerm(term) {
    this.setState({ term });
  }

  onChangePeriodicity(periodicity) {
    this.setState({ periodicity });
  }

  onSetupFeeChanged(setupFee) {
    this.setState({ setupFee });
  }

  onAdministrationFeeChanged(administrationFee) {
    this.setState({ administrationFee });
  }

  render() {
    const t = this.t;
    return (<div className={`${this.props.classPrefix}-interest-calculator`}>
      <Tabs>
        <TabList>
          <Tab><a className="btn">{t('Payment terms')}</a></Tab>
          <Tab><a className="btn">{t('Payment plan')}</a></Tab>
        </TabList>

        <TabPanel>
          <PaymentTermsTab
            {...this.props }
            t={t}
            formatter={this.formatter}
            percentFormatter={this.percentFormatter}
            onChangeTradeInIncVAT={this.onChangeTradeInIncVAT}
            onChangeDownPayment={this.onChangeDownPayment}
            onChangeAcquisitionPrice={this.onChangeAcquisitionPrice}
            onChangeInterest={this.onChangeInterest}
            onChangeTerm={this.onChangeTerm}
            onChangePeriodicity={this.onChangePeriodicity}
            onSetupFeeChanged={this.onSetupFeeChanged}
            onAdministrationFeeChanged={this.onAdministrationFeeChanged}
            amortizationAmount={this.calculateAmortizationAmount()}
            paymentMonthOneIncludingInterest={this.calculatePaymentMonthOneIncludingInterest()}
            increasedAmortizationAmount={this.calculateIncreasedAmortizationAmount()}
            acquisitionPrice={this.state.acquisitionPrice}
            tradeInIncVAT={this.state.tradeInIncVAT}
            downPayment={this.state.downPayment}
            creditAmount={this.state.creditAmount}
            interest={this.state.interest}
            term={this.state.term}
            periodicity={this.state.periodicity}
            setupFee={this.state.setupFee}
            administrationFee={this.state.administrationFee}
          />
        </TabPanel>
        <TabPanel>
          <AmortizationTab
            {...this.props }
            t={t}
            formatter={this.formatter}
            percentFormatter={this.percentFormatter}
            onChangeTradeInIncVAT={this.onChangeTradeInIncVAT}
            onChangeDownPayment={this.onChangeDownPayment}
            onChangeAcquisitionPrice={this.onChangeAcquisitionPrice}
            onChangeInterest={this.onChangeInterest}
            onChangeTerm={this.onChangeTerm}
            onChangePeriodicity={this.onChangePeriodicity}
            onSetupFeeChanged={this.onSetupFeeChanged}
            onAdministrationFeeChanged={this.onAdministrationFeeChanged}
            amortizationAmount={this.calculateAmortizationAmount()}
            paymentMonthOneIncludingInterest={this.calculatePaymentMonthOneIncludingInterest()}
            increasedAmortizationAmount={this.calculateIncreasedAmortizationAmount()}
            acquisitionPrice={this.state.acquisitionPrice}
            tradeInIncVAT={this.state.tradeInIncVAT}
            downPayment={this.state.downPayment}
            creditAmount={this.state.creditAmount}
            interest={this.state.interest}
            term={this.state.term}
            periodicity={this.state.periodicity}
            setupFee={this.state.setupFee}
            administrationFee={this.state.administrationFee}
          />
            </TabPanel>
      </Tabs>

      <div className={`${this.props.classPrefix}-footer`}><div>{this.props.cooperationName ? t('@cooperationName in cooperation with Nordea Finans', { '@cooperationName': this.props.cooperationName}) : t('Financing in cooperation with Nordea Finans')}</div>
    </div></div>);
  }
}
InterestCalculator.propTypes = {
  currency: PropTypes.object,
  acquisitionPrice: PropTypes.number,
  tradeInIncVAT: PropTypes.number,
  downPayment: PropTypes.number,
  creditAmount: PropTypes.number,
  interest: PropTypes.number,
  term: PropTypes.number,
  periodicity: PropTypes.string,
  classPrefix: PropTypes.string,
  setupFee: PropTypes.number,
  administrationFee: PropTypes.number,
  maxCreditAmount: PropTypes.number,
  vat: PropTypes.number,
  cooperationName: PropTypes.string,
  maxAcquisitionPrice: PropTypes.number,
  maxTradeInIncVAT: PropTypes.number,
  maxDownPayment: PropTypes.number,
  minInterest: PropTypes.number,
  maxInterest: PropTypes.number,
  maxTerms: PropTypes.number,
  stepAcquisitionPrice: PropTypes.number,
  stepTradeInIncVAT: PropTypes.number,
  stepDownPayment: PropTypes.number,
  stepInterest: PropTypes.number,
  stepTerms: PropTypes.number,
};

InterestCalculator.defaultProps = {
  currency: DEFAULT_CURRENCY,
  acquisitionPrice: 0,
  tradeInIncVAT: 0,
  downPayment: 0,
  creditAmount: 0,
  interest: 0,
  term: 0,
  periodicity: 'month',
  classPrefix: 'rn',
  setupFee: 0,
  administrationFee: 0,
  maxCreditAmount: 1000,
  vat: 25,
  cooperationName: null,
  maxAcquisitionPrice: 2500000,
  maxTradeInIncVAT: 2500000,
  maxDownPayment: 2500000,
  minInterest: 0,
  maxInterest: 10,
  maxTerms: 96,
  stepAcquisitionPrice: 10000,
  stepTradeInIncVAT: 10000,
  stepDownPayment: 10000,
  stepInterest: 0.01,
  stepTerms: 1,
};
